/**
 * Admin Audit Logs Page (PR A9)
 * View and filter audit logs with CSV export
 * All dates display in Eastern Time (ET), but queries use UTC
 * Global Column Sorting via useTableSort hook
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { 
  listAuditLogs, 
  exportAuditLogsCSV,
  getAuditResources,
  getAuditActions
} from '../utils/auditApi';
import AuditLogDrawer from '../components/AuditLogDrawer';
import useTableSort, { sortData } from '../hooks/useTableSort';
import './AdminAuditLogsPage.css';
import { formatDateTimeET } from '../utils/formatters';

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AdminAuditLogsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    query: searchParams.get('query') || '',
    resource: searchParams.get('resource') || '',
    action: searchParams.get('action') || '',
    actor_id: searchParams.get('actor_id') || '',
    impersonator_id: searchParams.get('impersonator_id') || '',
    result: searchParams.get('result') || '',
    only_impersonations: searchParams.get('only_impersonations') === 'true',
    page: parseInt(searchParams.get('page') || '1'),
    page_size: parseInt(searchParams.get('page_size') || '25')
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 25,
    total_items: 0,
    total_pages: 0
  });
  
  // Resources and actions for dropdowns
  const [resources, setResources] = useState([]);
  const [actions, setActions] = useState([]);
  
  // Debounced values for typeahead inputs (300ms)
  const debouncedActorId = useDebounce(filters.actor_id, 300);
  const debouncedImpersonatorId = useDebounce(filters.impersonator_id, 300);
  const debouncedQuery = useDebounce(filters.query, 300);
  
  const canView = hasPermission('audit', 'view');
  const canExport = hasPermission('audit', 'export');

  // Table sorting hook
  const {
    sortConfig,
    handleSort,
    getSortDirection
  } = useTableSort({
    defaultSortKey: 'occurred_at',
    defaultSortOrder: 'desc'
  });

  // Sort logs based on current sort configuration
  const sortedLogs = useMemo(() => {
    if (!logs.length) return logs;
    // Use sortConfig.key and sortConfig.direction (not sortKey/sortOrder)
    return sortData(logs, sortConfig.key, sortConfig.direction);
  }, [logs, sortConfig]);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Build query filters
      const queryFilters = { ...filters };
      
      // Convert ET datetime-local inputs to UTC ISO strings
      if (filters.date_from) {
        // Treat input as ET, convert to UTC
        const etDate = new Date(filters.date_from);
        queryFilters.date_from = etDate.toISOString();
      }
      
      if (filters.date_to) {
        const etDate = new Date(filters.date_to);
        queryFilters.date_to = etDate.toISOString();
      }
      
      // Handle "only impersonations" toggle
      if (filters.only_impersonations) {
        // This is a frontend filter - we'll filter after receiving results
        // Or we can set impersonator_id to a wildcard if API supports it
        // For now, we'll use a special marker
        queryFilters.only_impersonations_flag = true;
      }
      
      // Use debounced values
      queryFilters.actor_id = debouncedActorId;
      queryFilters.impersonator_id = debouncedImpersonatorId;
      queryFilters.query = debouncedQuery;
      
      // Remove empty values
      Object.keys(queryFilters).forEach(key => {
        if (queryFilters[key] === '' || queryFilters[key] === null || queryFilters[key] === undefined) {
          delete queryFilters[key];
        }
      });
      
      const response = await listAuditLogs(queryFilters);
      
      // Support both 'logs' and 'items' response keys for backward compatibility
      let logsData = response.items || response.logs || [];
      
      // Apply frontend-only filters
      if (filters.only_impersonations) {
        logsData = logsData.filter(log => log.impersonator_id && log.impersonator_id !== log.actor_id);
      }
      
      setLogs(logsData);
      setPagination(response.pagination || {
        page: 1,
        page_size: 25,
        total_items: logsData.length,
        total_pages: 1
      });
      
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [
    filters,
    debouncedActorId,
    debouncedImpersonatorId,
    debouncedQuery
  ]);

  // Fetch logs when debounced values or pagination changes
  useEffect(() => {
    if (!permissionsLoading && canView) {
      fetchLogs();
    } else if (!permissionsLoading && !canView) {
      setLoading(false);
    }
  }, [
    permissionsLoading, 
    canView, 
    fetchLogs
  ]);

  // Fetch resources and actions on mount
  useEffect(() => {
    if (!permissionsLoading && canView) {
      fetchResources();
      fetchActions();
    }
  }, [permissionsLoading, canView]);

  const fetchResources = async () => {
    try {
      const data = await getAuditResources();
      setResources(data);
    } catch (err) {
      console.error('Error fetching resources:', err);
    }
  };

  const fetchActions = async () => {
    try {
      const data = await getAuditActions();
      setActions(data);
    } catch (err) {
      console.error('Error fetching actions:', err);
    }
  };

  // Duplicate fetchLogs function removed - using the one defined above

  const updateFilters = (updates) => {
    const newFilters = { ...filters, ...updates };
    
    // Reset to page 1 when filters change (except pagination changes)
    if (!updates.page && !updates.page_size) {
      newFilters.page = 1;
    }
    
    setFilters(newFilters);
    
    // Update URL with all non-empty filters
    const params = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, value]) => {
      if (value && value !== '' && key !== 'page' && key !== 'page_size') {
        params.set(key, value.toString());
      }
    });
    
    // Always include page and page_size in URL
    params.set('page', newFilters.page.toString());
    params.set('page_size', newFilters.page_size.toString());
    
    setSearchParams(params);
  };

  const handleApplyFilters = () => {
    // Trigger fetch with current filters
    fetchLogs();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      date_from: '',
      date_to: '',
      query: '',
      resource: '',
      action: '',
      actor_id: '',
      impersonator_id: '',
      result: '',
      only_impersonations: false,
      page: 1,
      page_size: 25
    };
    setFilters(resetFilters);
    setSearchParams(new URLSearchParams());
    
    // Re-fetch with empty filters
    setTimeout(() => fetchLogs(), 100);
  };

  const handleExport = async () => {
    if (!canExport) {
      alert('You do not have permission to export audit logs.');
      return;
    }
    
    try {
      setExporting(true);
      
      // Use same filters as current view
      const exportFilters = { ...filters };
      
      // Convert dates to UTC
      if (filters.date_from) {
        const etDate = new Date(filters.date_from);
        exportFilters.date_from = etDate.toISOString();
      }
      
      if (filters.date_to) {
        const etDate = new Date(filters.date_to);
        exportFilters.date_to = etDate.toISOString();
      }
      
      await exportAuditLogsCSV(exportFilters);
      // Success handled in auditApi.js (download starts)
    } catch (err) {
      // Show user-friendly error
      if (err.message.includes('too large')) {
        alert('Export too large—narrow filters and try again.');
      } else if (err.message.includes('Rate limit')) {
        alert('Export rate limit exceeded. Please try again later.');
      } else {
        alert(`Export failed: ${err.message}`);
      }
    } finally {
      setExporting(false);
    }
  };

  const handleRowClick = (log) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => setSelectedLog(null), 300);
  };

  const handlePageChange = (newPage) => {
    updateFilters({ page: newPage });
  };

  const handlePageSizeChange = (e) => {
    updateFilters({ page_size: parseInt(e.target.value), page: 1 });
  };

  // Format datetime in Eastern Time
  const formatDateET = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDateTimeET(dateString);
    } catch (error) {
      return dateString;
    }
  };

  if (permissionsLoading) {
    return <div className="loading-container">Loading permissions...</div>;
  }

  if (!canView) {
    return (
      <div className="no-permission">
        <h2>Permission Denied</h2>
        <p>You don't have permission to view audit logs.</p>
      </div>
    );
  }

  return (
    <div className="admin-audit-logs-page">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb">
        <button className="breadcrumb-link" onClick={() => navigate('/dashboard')}>
          ← Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Audit Logs</span>
      </div>

      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Audit Logs</h1>
          <p className="page-description">View and export system activity logs</p>
        </div>
      </div>

      {/* Filters Panel */}
      <div className="filters-panel">
        {/* Row 1: Date Range & Search */}
        <div className="filter-row">
          <div className="filter-group">
            <label>From (ET)</label>
            <input
              type="datetime-local"
              className="filter-input"
              value={filters.date_from}
              onChange={(e) => updateFilters({ date_from: e.target.value })}
              title="Date range queries are executed in UTC"
            />
          </div>
          
          <div className="filter-group">
            <label>To (ET)</label>
            <input
              type="datetime-local"
              className="filter-input"
              value={filters.date_to}
              onChange={(e) => updateFilters({ date_to: e.target.value })}
              title="Date range queries are executed in UTC"
            />
          </div>
          
          <div className="filter-group">
            <label>Search</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search logs..."
              value={filters.query}
              onChange={(e) => updateFilters({ query: e.target.value })}
            />
          </div>
        </div>

        {/* Row 2: Resource, Action, Result */}
        <div className="filter-row">
          <div className="filter-group">
            <label>Resource</label>
            <select
              className="filter-select"
              value={filters.resource}
              onChange={(e) => updateFilters({ resource: e.target.value })}
            >
              <option value="">All Resources</option>
              {resources.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Action</label>
            <select
              className="filter-select"
              value={filters.action}
              onChange={(e) => updateFilters({ action: e.target.value })}
            >
              <option value="">All Actions</option>
              {actions.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Result</label>
            <select
              className="filter-select"
              value={filters.result}
              onChange={(e) => updateFilters({ result: e.target.value })}
            >
              <option value="">All Results</option>
              <option value="success">Success</option>
              <option value="failure">Failure</option>
            </select>
          </div>
        </div>

        {/* Row 3: Actor, Impersonator, Toggle */}
        <div className="filter-row">
          <div className="filter-group">
            <label>Actor (Email/ID)</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by actor..."
              value={filters.actor_id}
              onChange={(e) => updateFilters({ actor_id: e.target.value })}
            />
          </div>
          
          <div className="filter-group">
            <label>Impersonator (Email/ID)</label>
            <input
              type="text"
              className="filter-input"
              placeholder="Filter by impersonator..."
              value={filters.impersonator_id}
              onChange={(e) => updateFilters({ impersonator_id: e.target.value })}
            />
          </div>
          
          <div className="filter-group">
            <label>&nbsp;</label>
            <div className="filter-checkbox-group">
              <input
                type="checkbox"
                id="only-impersonations"
                checked={filters.only_impersonations}
                onChange={(e) => updateFilters({ only_impersonations: e.target.checked })}
              />
              <label htmlFor="only-impersonations">
                Only impersonations
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="filter-actions">
          <button className="btn-apply" onClick={handleApplyFilters}>
            Apply
          </button>
          <button className="btn-reset" onClick={handleResetFilters}>
            Reset
          </button>
          <button
            className="btn-export"
            onClick={handleExport}
            disabled={!canExport || exporting}
            title={!canExport ? 'You need audit:export permission' : 'Export filtered logs to CSV'}
          >
            {exporting ? 'Exporting...' : '📥 Export CSV'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <strong>Error:</strong> {error}
          <button 
            onClick={handleApplyFilters}
            style={{ 
              marginLeft: '12px', 
              padding: '4px 12px', 
              background: '#c33', 
              color: 'white', 
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-skeleton">
            <div className="skeleton-row"></div>
            <div className="skeleton-row"></div>
            <div className="skeleton-row"></div>
            <div className="skeleton-row"></div>
            <div className="skeleton-row"></div>
          </div>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="audit-logs-table sortable-table">
              <thead>
                <tr>
                  <th 
                    className={`sortable-header ${getSortDirection('occurred_at') ? 'sorted' : ''}`}
                    onClick={() => handleSort('occurred_at')}
                  >
                    <div className="th-content">
                      <span>Time (ET)</span>
                      {getSortDirection('occurred_at') && (
                        <span className="sort-indicator">{getSortDirection('occurred_at') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('actor_name') ? 'sorted' : ''}`}
                    onClick={() => handleSort('actor_name')}
                  >
                    <div className="th-content">
                      <span>Actor</span>
                      {getSortDirection('actor_name') && (
                        <span className="sort-indicator">{getSortDirection('actor_name') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Impersonator</th>
                  <th 
                    className={`sortable-header ${getSortDirection('resource') ? 'sorted' : ''}`}
                    onClick={() => handleSort('resource')}
                  >
                    <div className="th-content">
                      <span>Resource</span>
                      {getSortDirection('resource') && (
                        <span className="sort-indicator">{getSortDirection('resource') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('action') ? 'sorted' : ''}`}
                    onClick={() => handleSort('action')}
                  >
                    <div className="th-content">
                      <span>Action</span>
                      {getSortDirection('action') && (
                        <span className="sort-indicator">{getSortDirection('action') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Entity ID</th>
                  <th 
                    className={`sortable-header ${getSortDirection('result') ? 'sorted' : ''}`}
                    onClick={() => handleSort('result')}
                  >
                    <div className="th-content">
                      <span>Result</span>
                      {getSortDirection('result') && (
                        <span className="sort-indicator">{getSortDirection('result') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {sortedLogs.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  sortedLogs.map((log) => (
                    <tr key={log.id} onClick={() => handleRowClick(log)} className="clickable-row">
                      <td className="time-cell">
                        {formatDateET(log.occurred_at)}
                        <span className="et-badge">ET</span>
                      </td>
                      <td>
                        <div className="actor-cell">
                          <div className="actor-name">{log.actor_name || log.actor_id || 'N/A'}</div>
                          {log.actor_name && log.actor_id && (
                            <div className="actor-id">{log.actor_id}</div>
                          )}
                        </div>
                      </td>
                      <td>
                        {log.impersonator_id ? (
                          <span className="impersonation-badge">
                            🎭 {log.impersonator_name || log.impersonator_id}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span className="resource-badge">{log.resource}</span>
                      </td>
                      <td>
                        <span className="action-badge">{log.action}</span>
                      </td>
                      <td className="entity-cell">
                        {log.entity_id ? (
                          <span className="entity-id">{log.entity_id.substring(0, 8)}...</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {log.result ? (
                          <span className={`result-badge result-${log.result.toLowerCase()}`}>
                            {log.result === 'success' ? '✓' : '✗'} {log.result}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        <span className={`source-badge source-${log.source_collection === 'activity_events' ? 'activity' : 'audit'}`}>
                          {log.source_collection === 'activity_events' ? 'Activity' : 'Audit'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {sortedLogs.length === 0 && !loading && (
              <div className="empty-state">
                <p>No audit logs found for your current filters</p>
                <button className="btn-reset" onClick={handleResetFilters}>
                  Reset Filters
                </button>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="pagination">
              <button
                className="btn-page"
                onClick={() => handlePageChange(filters.page - 1)}
                disabled={filters.page === 1}
              >
                ← Previous
              </button>
              
              <div className="page-info">
                Page {filters.page} of {pagination.total_pages}
                ({pagination.total_items} total)
              </div>
              
              <select
                className="page-size-select"
                value={filters.page_size}
                onChange={handlePageSizeChange}
              >
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
              
              <button
                className="btn-page"
                onClick={() => handlePageChange(filters.page + 1)}
                disabled={filters.page >= pagination.total_pages}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Audit Log Details Drawer */}
      <AuditLogDrawer
        log={selectedLog}
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
      />
    </div>
  );
};

export default AdminAuditLogsPage;
